import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Card from '../components/ui/Card';
import { api, ApiClientError } from '../utils/api';
import { useAuth } from '../context/AuthContext';

type ProductType = 'planning_report' | 'compliance_pack';
type GenerationStatus = 'pending' | 'generating' | 'complete' | 'failed';

const PROGRESS_STEPS_REPORT = [
  'Fetching planning data...',
  'Checking heritage records...',
  'Generating PDF...',
];

const PROGRESS_STEPS_PACK = [
  'Processing uploaded documents...',
  'Classifying document content...',
  'Generating form answers...',
  'Assembling PDF bundle...',
];

/**
 * Derive the canonical product price in pence from the SKU. Used when
 * the URL lacks an `amount` query param — that field is now optional
 * because the server-side Checkout flow resolves the amount from
 * `productType` alone (Req 3.3), and the round-trip through
 * `success_url` no longer needs to carry it.
 */
function deriveAmount(type: ProductType): number {
  // Buyer's Report (Feature 36): planning_report priced at £29 (canonical
  // 2900 pence); compliance_pack stays at £79.
  return type === 'compliance_pack' ? 7900 : 2900;
}

function formatPrice(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

function getProductLabel(type: ProductType): string {
  return type === 'planning_report' ? "Buyer's Report" : "Seller's Pack";
}

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const productType = (params.get('type') || 'planning_report') as ProductType;
  const productId = params.get('id') || '';
  // Stripe's success_url carries `session_id={CHECKOUT_SESSION_ID}`. We
  // surface it only for logging / debugging — it is NEVER used as proof
  // of payment (Req 8.x: polling on `isPaid` is the only payment gate).
  const stripeCheckoutSessionId = params.get('session_id') || undefined;
  // `amount` becomes optional: derive from `type` when absent so callers
  // that no longer set it (the new Stripe-Checkout path) render the same
  // summary as before. A 0/absent URL param also falls through to the
  // derived value.
  const rawAmount = parseInt(params.get('amount') || '0', 10);
  const amount = rawAmount > 0 ? rawAmount : deriveAmount(productType);
  const address = params.get('address') || undefined;

  const [status, setStatus] = useState<GenerationStatus>('pending');
  const [progressStep, setProgressStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Validate we have required params
  useEffect(() => {
    if (!productId) {
      navigate('/', { replace: true });
    }
  }, [productId, navigate]);

  // Capture Stripe's Checkout Session ID for observability. Req 8 is
  // explicit that this value MUST NEVER be used as proof of payment —
  // polling on `isPaid` is the sole gate. Log once per mount.
  useEffect(() => {
    if (stripeCheckoutSessionId) {
      // eslint-disable-next-line no-console
      console.log('[OrderConfirmation] Stripe session returned', {
        stripeCheckoutSessionId,
        productType,
        productId,
      });
    }
  }, [stripeCheckoutSessionId, productType, productId]);

  // Poll for completion
  useEffect(() => {
    if (!productId) return;

    let cancelled = false;
    let pollCount = 0;
    const maxPolls = 60; // 5 minutes at 5s intervals

    const progressSteps = productType === 'planning_report'
      ? PROGRESS_STEPS_REPORT
      : PROGRESS_STEPS_PACK;

    // Simulate progress steps while polling
    const progressInterval = setInterval(() => {
      if (cancelled) return;
      setProgressStep(prev => {
        if (prev < progressSteps.length - 1) return prev + 1;
        return prev;
      });
    }, productType === 'planning_report' ? 3000 : 8000);

    setStatus('generating');

    const poll = async () => {
      if (cancelled || pollCount >= maxPolls) {
        if (pollCount >= maxPolls) {
          // After 60 polls (~5 minutes) without the webhook marking the
          // product as paid, show a reassuring fallback rather than a
          // failure message. The payment itself has already been confirmed
          // by Stripe (we wouldn't be on this page otherwise) — the only
          // thing that has slipped is the async fulfilment. Re-downloads
          // from the account page will pick it up once the webhook lands.
          // See Req 4.5.
          setError(
            'Your payment was received. Fulfilment is taking longer than usual — ' +
            'check your account page in a few minutes, or keep this tab open; ' +
            'it will continue to update automatically. You will also receive a ' +
            'confirmation email when the download is ready.'
          );
          setStatus('failed');
        }
        return;
      }

      pollCount++;

      try {
        if (productType === 'planning_report') {
          const data = await api.getPlanningReport(productId);
          if (data.report?.isPaid) {
            if (!cancelled) {
              setStatus('complete');
              setProgressStep(progressSteps.length - 1);
            }
            return;
          }
        } else {
          // Compliance pack: poll CompliancePack.isPaid directly via the
          // pack-keyed endpoint. We deliberately do NOT read UploadSession
          // status here — that partition is NOT flipped on payment; only
          // the CompliancePack.isPaid flag is the source of truth (Req 9.2).
          //
          // The packId follows the PACK-${sessionId} convention written
          // by generate-compliance-pack.ts.
          const packId = `PACK-${productId}`;
          const data = await api.getCompliancePack(packId);
          // Top-level `isPaid` is the canonical surface. The nested
          // `pack.isPaid` is a defensive fallback for older responses.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const paid: boolean = data.isPaid === true || data.pack?.isPaid === true;
          if (paid) {
            if (!cancelled) {
              setStatus('complete');
              setProgressStep(progressSteps.length - 1);
            }
            return;
          }
        }
      } catch (err) {
        // 404 or transient errors — keep polling. The pack record may
        // not exist yet if the user is here mid-Stripe-redirect or if the
        // backend is still finishing generation.
        if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
          // Still generating / not yet written.
        }
      }

      if (!cancelled) {
        setTimeout(poll, 5000);
      }
    };

    // Start polling after a short delay (give webhook time to process)
    const initialDelay = setTimeout(poll, 2000);

    return () => {
      cancelled = true;
      clearInterval(progressInterval);
      clearTimeout(initialDelay);
    };
  }, [productId, productType]);

  const progressSteps = productType === 'planning_report'
    ? PROGRESS_STEPS_REPORT
    : PROGRESS_STEPS_PACK;

  const viewResultUrl = productType === 'planning_report'
    ? `/report/planning/${productId}`
    : `/selling-documents?packId=${productId}&paid=true`;

  return (
    <>
      <Helmet>
        <title>Order Confirmation | UKPropertyChecks</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        <div className="container-main section-spacing">
          <div className="max-w-2xl mx-auto space-y-6">

            {/* Confirmation header */}
            <Card>
              <div className="text-center">
                {status === 'complete' ? (
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-4" aria-hidden="true">
                    <svg className="w-6 h-6 text-emerald-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : (
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-4" aria-hidden="true">
                    <svg className="w-6 h-6 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                )}

                <h1 className="text-2xl font-semibold text-slate-950">
                  Payment received
                </h1>
                <p className="mt-2 text-slate-600">
                  {status === 'complete'
                    ? `Your ${getProductLabel(productType).toLowerCase()} is ready.`
                    : `Your ${getProductLabel(productType).toLowerCase()} is being generated.`}
                </p>
              </div>
            </Card>

            {/* Order summary */}
            <Card>
              <h2 className="text-lg font-semibold text-slate-950 mb-4">Order summary</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-600">Product</dt>
                  <dd className="text-slate-950 font-medium">{getProductLabel(productType)}</dd>
                </div>
                {amount > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-slate-600">Amount paid</dt>
                    <dd className="text-slate-950 font-medium">{formatPrice(amount)}</dd>
                  </div>
                )}
                {address && (
                  <div className="flex justify-between">
                    <dt className="text-slate-600">Property</dt>
                    <dd className="text-slate-950 text-right max-w-[60%]">{address}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-600">Date</dt>
                  <dd className="text-slate-950">
                    {new Date().toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </dd>
                </div>
              </dl>
            </Card>

            {/* Generation progress */}
            {status !== 'complete' && status !== 'failed' && (
              <Card>
                <h2 className="text-lg font-semibold text-slate-950 mb-4">Generating your {getProductLabel(productType).toLowerCase()}</h2>
                <div className="space-y-3">
                  {progressSteps.map((step, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {index < progressStep ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 shrink-0" aria-hidden="true">
                          <svg className="w-4 h-4 text-emerald-700" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : index === progressStep ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 shrink-0" aria-hidden="true">
                          <span className="w-3 h-3 rounded-full bg-emerald-600 animate-pulse" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 shrink-0" aria-hidden="true">
                          <span className="w-2 h-2 rounded-full bg-slate-300" />
                        </span>
                      )}
                      <span className={`text-sm ${index <= progressStep ? 'text-slate-950' : 'text-slate-400'}`}>
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-600" role="status" aria-live="polite">
                  This usually takes less than {productType === 'planning_report' ? '30 seconds' : '2 minutes'}.
                </p>
              </Card>
            )}

            {/* Error state */}
            {status === 'failed' && error && (
              <Card>
                <div className="border-l-4 border-red-600 pl-4" role="alert">
                  <h2 className="text-lg font-semibold text-red-900">There is a problem</h2>
                  <p className="mt-1 text-sm text-red-800">{error}</p>
                </div>
              </Card>
            )}

            {/* Ready — view result */}
            {status === 'complete' && (
              <Card>
                <div className="text-center space-y-4">
                  <p className="text-slate-800">Your {getProductLabel(productType).toLowerCase()} is ready to view and download.</p>
                  <Link to={viewResultUrl} className="btn-primary inline-block">
                    View {getProductLabel(productType).toLowerCase()}
                  </Link>
                </div>
              </Card>
            )}

            {/* Next steps */}
            <Card variant="subtle">
              <h2 className="text-lg font-semibold text-slate-950 mb-3">What happens next</h2>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5" aria-hidden="true">•</span>
                  <span>You will receive a confirmation email with a download link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5" aria-hidden="true">•</span>
                  <span>Your {getProductLabel(productType).toLowerCase()} is available for 90 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5" aria-hidden="true">•</span>
                  <span>You can re-download from your account at any time</span>
                </li>
              </ul>
            </Card>

            {/* Account link */}
            {isAuthenticated && (
              <div className="text-center">
                <Link to="/account" className="text-sm text-blue-700 hover:text-blue-900 underline">
                  Go to your account
                </Link>
              </div>
            )}

            {!isAuthenticated && (
              <Card variant="subtle">
                <p className="text-sm text-slate-700">
                  <Link to="/login" className="text-blue-700 hover:text-blue-900 underline">Sign in</Link> to save this to your account and access it from any device.
                </p>
              </Card>
            )}

            {/* Disclaimer */}
            <p className="text-xs text-slate-600 text-center">
              Information and document preparation only. Not legal advice.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}
